import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Scale } from "lucide-react";
import { api } from "@/api";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";

export default function Impact() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const metrics = (settings?.impactMetrics || []).filter((m) => m.value);

  return (
    <div data-testid="impact-page">
      <PageHero
        eyebrow="Impact"
        title="Impact, measured honestly."
        lede="We believe credibility matters more than numbers on a page. Impact metrics are published only when they are real, verified and meaningful."
      />

      <section className="py-24 lg:py-32" data-testid="impact-content">
        <div className="container-rcs">
          {metrics.length > 0 ? (
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-navy-900/10 bg-navy-900/10 md:grid-cols-4">
              {metrics.map((m, i) => (
                <div key={m.label} className="bg-white p-8" data-testid={`impact-metric-${i}`}>
                  <p className="font-serif text-3xl text-navy-900 lg:text-4xl">{m.value}</p>
                  <p className="mt-3 text-xs uppercase tracking-[0.2em] text-slate-500">{m.label}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-12 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <Section
                  eyebrow="Our Approach to Impact"
                  title="A young organisation, building deliberately."
                />
                <Reveal delay={0.1}>
                  <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-600">
                    <p>
                      Roy Career Solutions was incorporated in October 2024. Rather than publish
                      aspirational figures, we choose to share our impact as it becomes real —
                      learners enrolled, classes delivered, educators associated and learning hours
                      completed across our brands.
                    </p>
                    <p>
                      Our commitment is to report impact with the same integrity we expect in our
                      classrooms: accurate, verifiable and meaningful.
                    </p>
                  </div>
                </Reveal>
              </div>
              <Reveal delay={0.2} className="lg:col-span-5">
                <div className="rounded-2xl bg-navy-950 p-10" data-testid="impact-principles">
                  <Scale className="h-7 w-7 text-gold" aria-hidden />
                  <h3 className="mt-6 font-serif text-2xl text-ivory">What we will measure</h3>
                  <ul className="mt-6 space-y-3 text-sm text-slate-300">
                    {["Learners enrolled", "Classes delivered", "Teachers associated", "Courses offered", "Cities reached", "Learning hours", "Student satisfaction"].map((m) => (
                      <li key={m} className="flex items-center gap-3">
                        <span className="h-1.5 w-1.5 rounded-full bg-gold" aria-hidden />
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </section>

      <section className="border-t border-navy-900/10 bg-white py-20" data-testid="impact-cta-section">
        <div className="container-rcs flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="max-w-xl font-serif text-3xl text-navy-900">See the brands creating that impact.</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/brands" className="btn-gold" data-testid="impact-brands-cta">
              Explore Our Brands <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
